// API & AI 页面的静态内容：AI 助手连接步骤、MCP 工具清单、API 接口清单、示例代码。
// 工具与接口须与 partner-api（vtm-mcp/src/tools.ts、controller/*）保持一致。

// 连接器每次登录都会以此前缀新建一把密钥（vtm-mcp/src/vtm.ts），据此在列表里标成「AI 助手」
// 后端把 Long 序列化成字符串，计数一律转成数字再用
export function toCount(value) {
  const n = Number(value);
  return isFinite(n) ? n : 0;
}

// 后端日期是不带时区的 UTC 字符串 "yyyy-MM-dd HH:mm:ss"
export function parseServerDate(value) {
  if (!value) {
    return null;
  }
  if (typeof value === "number") {
    return new Date(value);
  }
  const iso = String(value).replace(" ", "T");
  return new Date(/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : iso + "Z");
}

export function normalizeUsage(data) {
  const rows = list => (list || []).map(r => ({ ...r, calls: toCount(r.calls) }));
  return {
    ...data,
    today: toCount(data.today),
    thisMonth: toCount(data.thisMonth),
    total: toCount(data.total),
    daily: rows(data.daily),
    byKey: rows(data.byKey),
    byMachine: rows(data.byMachine),
    byCustomer: rows(data.byCustomer).map(c => ({ ...c, keys: toCount(c.keys), machines: toCount(c.machines) }))
  };
}

export const CONNECTOR_LABEL_PREFIX = "Claude connector";

export function isConnectorKey(key) {
  return !!(key && key.label && key.label.indexOf(CONNECTOR_LABEL_PREFIX) === 0);
}

// MCP 工具（全部只读）
export const MCP_TOOLS = [
  {
    name: "list_machines",
    title: "List machines",
    description: "Your machines with name, location, online/offline status, last seen time and subscription expiry.",
    ask: "Which of my machines are offline right now?"
  },
  {
    name: "get_machine",
    title: "Get machine",
    description: "Details for one machine: name, location, status, last seen and subscription expiry.",
    ask: "When was the Main Street machine last online?"
  },
  {
    name: "get_inventory",
    title: "Get inventory",
    description: "Aisle-by-aisle stock: product, current and max stock, low-stock threshold, price and aisle faults.",
    ask: "What needs restocking before Friday?"
  },
  {
    name: "list_orders",
    title: "List orders",
    description: "Individual sales: product, aisle, quantity, price paid, payment status and time. Filter by machine and dates.",
    ask: "Show me yesterday's sales on my airport machine."
  },
  {
    name: "get_order",
    title: "Get order",
    description: "One order by its order number.",
    ask: "Look up order 1306259111836651520."
  },
  {
    name: "get_sales_summary",
    title: "Sales summary",
    description: "Completed-sale count and revenue, in total and per machine (best seller first), for any date range.",
    ask: "Which machine made the most money last month?"
  }
];

// AI 助手连接步骤。{url} 在渲染时替换为 MCP 地址
export function mcpClients(mcpUrl) {
  const cursorConfig = window.btoa(JSON.stringify({ url: mcpUrl }));
  const vscodeConfig = encodeURIComponent(JSON.stringify({ name: "vtm", type: "http", url: mcpUrl }));
  return [
    {
      id: "claude",
      name: "Claude",
      hint: "claude.ai and the Claude desktop app",
      steps: [
        "In Claude, open <b>Settings → Connectors</b>.",
        "Click <b>Add custom connector</b>.",
        "Name it <b>VTM Vending</b> and paste the MCP link above.",
        "Click <b>Continue</b>, then <b>Connect</b>, and sign in with your VTM email and password.",
        "Start a chat and ask about your machines. Claude asks before using a tool the first time."
      ]
    },
    {
      id: "chatgpt",
      name: "ChatGPT",
      hint: "Plugins",
      steps: [
        "Open <b>Plugins</b> from the ChatGPT sidebar.",
        "Click <b>Add (+)</b> in the upper-right corner.",
        "Select <b>Add custom MCP server</b>.",
        "Set <b>Name</b> to <b>VTM Vending</b>.",
        "Under <b>Connection</b>, select <b>Server URL</b> and enter <b>" + mcpUrl + "</b>.",
        "Set <b>Authentication</b> to <b>OAuth</b>.",
        "Review the security notice and select <b>I understand and want to continue</b>.",
        "Click <b>Create as a plugin</b> and follow the authentication prompts."
      ]
    },
    {
      id: "claude-code",
      name: "Claude Code",
      hint: "Terminal",
      steps: ["Run this in your terminal:", "Then run <b>/mcp</b> inside Claude Code and choose <b>vtm</b> to sign in."],
      command: "claude mcp add --transport http vtm " + mcpUrl
    },
    {
      id: "cursor",
      name: "Cursor",
      hint: "One click",
      steps: ["Click the button to add VTM to Cursor, then click <b>Connect</b> next to it in Cursor's MCP settings and sign in."],
      installUrl: "cursor://anysphere.cursor-deeplink/mcp/install?name=vtm&config=" + cursorConfig,
      installLabel: "Add to Cursor"
    },
    {
      id: "vscode",
      name: "VS Code",
      hint: "One click",
      steps: ["Click the button to add VTM to VS Code, then start the server from the MCP view and sign in."],
      installUrl: "vscode:mcp/install?" + vscodeConfig,
      installLabel: "Add to VS Code"
    },
    {
      id: "other",
      name: "Other",
      hint: "Any MCP client",
      steps: [
        "Any assistant that supports remote MCP servers with sign-in (OAuth) can connect. Most accept a config like this:",
        "When it asks you to sign in, use your VTM email and password."
      ],
      command: JSON.stringify({ mcpServers: { vtm: { url: mcpUrl } } }, null, 2)
    }
  ];
}

// 公开的只读接口（与 docs/openapi.json 一致；写接口仅限员工密钥，不在此列）
export const API_ENDPOINTS = [
  {
    group: "Machines",
    items: [
      { method: "GET", path: "/api/v1/machines", summary: "List your machines", params: "status (online | offline), page, size (max 100)" },
      { method: "GET", path: "/api/v1/machines/{machineNo}", summary: "One machine's details", params: "" }
    ]
  },
  {
    group: "Inventory",
    items: [
      { method: "GET", path: "/api/v1/inventory", summary: "Aisle stock across your machines", params: "machineNos (optional, repeat or comma-separate)" },
      { method: "GET", path: "/api/v1/inventory/machines/{machineNo}", summary: "Aisle stock for one machine", params: "" }
    ]
  },
  {
    group: "Orders",
    items: [
      { method: "GET", path: "/api/v1/orders", summary: "List orders, newest first", params: "machineNo, startTime, endTime (yyyy-MM-dd HH:mm:ss), page, size (max 100)" },
      { method: "GET", path: "/api/v1/orders/{orderNumber}", summary: "One order with its items", params: "" },
      { method: "GET", path: "/api/v1/orders/summary", summary: "Sales count and revenue, per machine", params: "startTime, endTime (yyyy-MM-dd HH:mm:ss)" }
    ]
  }
];

export const API_ERRORS = [
  { code: 401, meaning: "The X-API-Key header is missing, or the key is wrong or revoked." },
  { code: 403, meaning: "No machine on the account has an active subscription, or the key lacks the scope for this call." },
  { code: 404, meaning: "The machine or order does not exist on your account." },
  { code: 429, meaning: "Too many calls: over your hourly allowance (1,000 per paid machine, shared by all your keys) or over 20 a second. Wait for the number of seconds in Retry-After." }
];

// 示例代码。key 为空时使用占位符
export function codeSamples(baseUrl, key) {
  const k = key || "YOUR_API_KEY";
  return [
    {
      id: "curl",
      label: "cURL",
      code: `curl "${baseUrl}/api/v1/machines?status=offline" \\\n  -H "X-API-Key: ${k}"`
    },
    {
      id: "js",
      label: "JavaScript",
      code:
        `const res = await fetch("${baseUrl}/api/v1/machines?status=offline", {\n` +
        `  headers: { "X-API-Key": "${k}" }\n` +
        `});\n` +
        `const { data } = await res.json();\n` +
        `console.log(data.records);`
    },
    {
      id: "python",
      label: "Python",
      code:
        `import requests\n\n` +
        `res = requests.get(\n` +
        `    "${baseUrl}/api/v1/machines",\n` +
        `    params={"status": "offline"},\n` +
        `    headers={"X-API-Key": "${k}"},\n` +
        `)\n` +
        `print(res.json()["data"]["records"])`
    }
  ];
}

export const SAMPLE_RESPONSE = `{
  "code": 200,
  "message": "success",
  "data": {
    "records": [
      {
        "machineNo": "866903013700011",
        "name": "Main Street",
        "status": "offline",
        "lastSeen": "2026-10-26T08:14:02.000+00:00"
      }
    ],
    "total": 1,
    "page": 1,
    "size": 20
  }
}`;
